// Inline Event Manager picker for CREATE surfaces (Feb 2026).
//
// Different from EventManagerAssignment.jsx: this is a stateless
// controlled field intended for use INSIDE an admin create form/dialog
// BEFORE the event exists. It lists active-member candidates and
// surfaces the current pick as a chip; the parent form owns the
// selectedProfileId state and calls the universal assignment API
// after successful create.
//
// UX rules baked in:
//   • Optional — parent may submit with null selection.
//   • Admin may pick themselves.
//   • Only ACTIVE members appear (account_status IN approved/active).
//   • Search-as-you-type, remote-fed for consistency with the
//     universal candidate endpoint.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminAPI } from '../../lib/api';
import { X, ShieldCheck, MagnifyingGlass } from '@phosphor-icons/react';

/**
 * @param {object}   props
 * @param {string|null} props.value              Selected profile_id (or null).
 * @param {(profileId:string|null,name:string|null)=>void} props.onChange
 * @param {string}   [props.label]               Field label (default "Event Manager").
 * @param {string}   [props.helperText]
 * @param {string}   [props.testIdPrefix]        Prefix for data-testids.
 */
export default function ManagerSelectField({
  value,
  onChange,
  label = 'Event Manager',
  helperText = 'Optional. Assigns full event-scoped management rights. You can change or remove later.',
  testIdPrefix = 'mgr-select',
}) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState(null);
  const rootRef = useRef(null);

  // When `value` changes externally (e.g. parent reset), also refresh
  // the display name.
  const refreshSelectedName = useCallback(async () => {
    if (!value) { setSelectedName(null); return; }
    // Rely on the candidates cache first — cheap.
    const hit = candidates.find((c) => c.profile_id === value);
    if (hit) { setSelectedName(hit.name); return; }
    // Otherwise fetch a fresh page keyed by the value's likely search.
    try {
      const r = await adminAPI.listEventManagerCandidates('');
      const list = r.data?.candidates || [];
      const found = list.find((c) => c.profile_id === value);
      setSelectedName(found?.name || 'Selected member');
    } catch {
      setSelectedName('Selected member');
    }
  }, [value, candidates]);

  useEffect(() => { refreshSelectedName(); }, [refreshSelectedName]);

  // Load candidates lazily when the panel opens or the query changes.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoading(true);
    adminAPI.listEventManagerCandidates(q)
      .then((r) => { if (!cancel) setCandidates(r.data?.candidates || []); })
      .catch(() => { if (!cancel) setCandidates([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, q]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (c) => {
    onChange?.(c.profile_id, c.name);
    setSelectedName(c.name);
    setOpen(false);
    setQ('');
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(null, null);
    setSelectedName(null);
  };

  return (
    <div className="space-y-1.5" ref={rootRef}>
      <label className="text-xs font-medium text-[#013523] flex items-center gap-1.5">
        <ShieldCheck size={12} weight="fill" className="text-[#015e3f]" />
        {label}
      </label>

      <button
        type="button"
        data-testid={`${testIdPrefix}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 border border-[#e3dcd1] rounded-lg bg-white text-sm text-[#013523] hover:border-[#015e3f]/50 flex items-center justify-between gap-2"
      >
        <span className="truncate">
          {value ? (selectedName || 'Selected member') : (
            <span className="text-[#6a766e]">No Event Manager</span>
          )}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => { if (e.key === 'Enter') clear(e); }}
            aria-label="Clear Event Manager"
            data-testid={`${testIdPrefix}-clear`}
            className="shrink-0 text-[#6a766e] hover:text-[#7a3a3a] p-0.5 rounded cursor-pointer"
          >
            <X size={12} weight="bold" />
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid={`${testIdPrefix}-panel`}
          className="border border-[#e3dcd1] rounded-lg bg-white shadow-lg p-2 space-y-2"
        >
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6a766e]" />
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search active members…"
              data-testid={`${testIdPrefix}-search`}
              className="w-full pl-7 pr-2 py-1.5 text-sm bg-[#fbfaf8] border border-[#e3dcd1] rounded focus:outline-none focus:ring-1 focus:ring-[#015e3f]/40"
            />
          </div>
          <ul
            className="max-h-52 overflow-y-auto space-y-0.5"
            data-testid={`${testIdPrefix}-list`}
          >
            {loading ? (
              <li className="text-xs text-[#6a766e] px-2 py-1.5">Loading…</li>
            ) : candidates.length === 0 ? (
              <li className="text-xs text-[#6a766e] px-2 py-1.5">No matching active members.</li>
            ) : (
              candidates.map((c) => (
                <li key={c.profile_id}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    data-testid={`${testIdPrefix}-pick-${c.profile_id}`}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm text-[#013523] hover:bg-[#f0ece3]/60 ${
                      c.profile_id === value ? 'bg-[#015e3f]/10' : ''
                    }`}
                  >
                    {c.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {helperText && (
        <p className="text-[11px] text-[#6a766e]">{helperText}</p>
      )}
    </div>
  );
}
